import { useState } from 'react'
import { X, Zap, Check, Crown, Rocket, Building2 } from 'lucide-react'
import api from '../services/api'
import { useLanguage } from '../hooks/useLanguage'
import { useToast } from '../hooks/useToast'

interface UpgradeModalProps {
  isOpen: boolean
  onClose: () => void
  trialExpired?: boolean
  trialDaysLeft?: number | null
  currentPlan?: string
}

const plans = [
  {
    id: 'starter',
    name: 'Starter',
    icon: Rocket,
    monthlyPrice: 29,
    yearlyPrice: 23,
    yearlyTotal: 279,
    features: [
      '3 workspace',
      '3.000 transazioni/mese',
      'Categorizzazione AI',
      'Dashboard + Report',
      '1 integrazione'
    ],
    color: 'blue'
  },
  {
    id: 'pro',
    name: 'Pro',
    icon: Crown,
    monthlyPrice: 49,
    yearlyPrice: 39,
    yearlyTotal: 470,
    popular: true,
    features: [
      '10 workspace',
      '15.000 transazioni/mese',
      'Chat AI avanzata (150 msg/giorno)',
      'Dashboard + Previsioni',
      'Tutte le integrazioni'
    ],
    color: 'primary'
  },
  {
    id: 'business',
    name: 'Business',
    icon: Building2,
    monthlyPrice: 99,
    yearlyPrice: 79,
    yearlyTotal: 950,
    features: [
      '100 workspace',
      '100.000 transazioni/mese',
      'Tutto di Pro +',
      'Multi-utente (team)',
      'Registro Imprese + API',
      'Chat AI (500 msg/giorno)'
    ],
    color: 'purple'
  }
]

export default function UpgradeModal({ isOpen, onClose, trialExpired, trialDaysLeft, currentPlan }: UpgradeModalProps) {
  useLanguage() // For future translations
  const { showToast } = useToast()
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('yearly')
  const [loading, setLoading] = useState<string | null>(null)
  const isTrialActive = !trialExpired && trialDaysLeft !== null && trialDaysLeft !== undefined

  if (!isOpen) return null

  const handleUpgrade = async (planId: string) => {
    setLoading(planId)
    try {
      const response = await api.post('/stripe/create-checkout', {
        plan: planId,
        interval: billingInterval
      })
      // Redirect to Stripe Checkout
      window.location.href = response.data.url
    } catch (error) {
      console.error('Failed to create checkout session:', error)
      showToast('error', 'Errore durante la creazione del checkout. Riprova.')
    } finally {
      setLoading(null)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-modal max-w-4xl w-full max-h-[90vh] overflow-auto animate-scaleIn">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <Zap className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {trialExpired ? 'Il tuo periodo di prova è terminato' : 'Passa a un piano a pagamento'}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {trialExpired 
                  ? 'Scegli un piano per continuare a usare PongoFin'
                  : isTrialActive
                    ? `Ti restano ${trialDaysLeft} giorni di prova sul piano attuale`
                    : 'Sblocca tutte le funzionalità'
                }
              </p>
            </div>
          </div>
          {!trialExpired && (
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center py-6">
          <div className="inline-flex items-center gap-4 bg-gray-100 dark:bg-slate-700 p-1 rounded-xl">
            <button
              onClick={() => setBillingInterval('monthly')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                billingInterval === 'monthly'
                  ? 'bg-white dark:bg-slate-600 text-gray-900 dark:text-white shadow'
                  : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              Mensile
            </button>
            <button
              onClick={() => setBillingInterval('yearly')}
              className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                billingInterval === 'yearly'
                  ? 'bg-white dark:bg-slate-600 text-gray-900 dark:text-white shadow'
                  : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              Annuale <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-400 px-2 py-0.5 rounded-full">-20%</span>
            </button>
          </div>
        </div>

        {/* Plans */}
        <div className="grid md:grid-cols-3 gap-6 p-6 pt-0">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-2xl p-6 ${
                plan.popular
                  ? 'bg-gradient-to-b from-primary-600 to-primary-700 text-white ring-4 ring-primary-200 dark:ring-primary-900/50'
                  : 'bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-yellow-400 text-yellow-900 text-xs font-bold px-3 py-1 rounded-full">
                  Più Popolare
                </span>
                </div>
              )}

              <div className="flex items-center gap-2 mb-4">
                <plan.icon className={`w-5 h-5 ${plan.popular ? 'text-white' : 'text-gray-600 dark:text-gray-400'}`} />
                <h3 className={`text-lg font-bold ${plan.popular ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                  {plan.name}
                </h3>
              </div>

              <div className="mb-6">
                <span className={`text-3xl font-bold ${plan.popular ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                  €{billingInterval === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice}
                </span>
                <span className={plan.popular ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}>/mese</span>
                {billingInterval === 'yearly' && (
                  <p className={`text-sm mt-1 ${plan.popular ? 'text-white/70' : 'text-gray-400'}`}>
                    Fatturato €{plan.yearlyTotal}/anno
                  </p>
                )}
              </div>

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                      plan.popular ? 'text-yellow-300' : 'text-green-500'
                    }`} />
                    <span className={`text-sm ${plan.popular ? 'text-white' : 'text-gray-600 dark:text-gray-300'}`}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleUpgrade(plan.id)}
                disabled={loading !== null || currentPlan === plan.id}
                className={`w-full py-3 px-4 rounded-xl font-semibold transition-all ${
                  plan.popular
                    ? 'bg-white text-primary-700 hover:bg-primary-50'
                    : 'bg-primary-600 text-white hover:bg-primary-700'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {loading === plan.id ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Caricamento...
                  </span>
                ) : currentPlan === plan.id ? (
                  'Piano attuale'
                ) : (
                  'Scegli questo piano'
                )}
              </button>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-6 pt-0 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            ✅ Pagamento sicuro con Stripe • ⏱️ 14 giorni gratis • 📧 Fattura disponibile
          </p>
        </div>
      </div>
    </div>
  )
}
